import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import { localizationConfig } from "./localization-config";
import { getPreferredLocale, setLocaleCookie } from "./locale-cookie";
import type { I18nBundle } from "./resources";
import { ALL_LOCALES, DEFAULT_LOCALE, type Locale } from "./locales";

const SUPPORTED_LOCALES: readonly Locale[] = ALL_LOCALES;

let activeBundle: I18nBundle;

function loadLocaleBundle(lng: string): Promise<void> {
  if (
    lng === DEFAULT_LOCALE ||
    !SUPPORTED_LOCALES.includes(lng as Locale) ||
    i18n.hasResourceBundle(lng, "translation")
  ) {
    return Promise.resolve();
  }

  return Promise.resolve(activeBundle.loaders[lng]?.() ?? {}).then(
    (translations) => {
      i18n.addResourceBundle(lng, "translation", translations, true, true);
      i18n.emit("languageChanged", lng);
    },
  );
}

export function initI18n(bundle: I18nBundle) {
  activeBundle = bundle;
  const locale = getPreferredLocale(SUPPORTED_LOCALES);

  i18n.on("languageChanged", (lng) => {
    if (localizationConfig.isEnabled()) {
      setLocaleCookie(lng);
    }
    void loadLocaleBundle(lng);
  });

  void i18n
    .use(initReactI18next)
    .init({
      resources: { [DEFAULT_LOCALE]: { translation: bundle.en } },
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      keySeparator: false,
      nsSeparator: false,
      contextSeparator: "__rc__",
      pluralSeparator: "__rp__",
      interpolation: { escapeValue: false },
      react: { useSuspense: false },
      returnEmptyString: false,
    })
    .then(() => loadLocaleBundle(locale));
}

/** Loads the locale bundle before switching to avoid an English flash. */
export async function setLocale(locale: Locale): Promise<void> {
  if (
    locale !== DEFAULT_LOCALE &&
    !i18n.hasResourceBundle(locale, "translation")
  ) {
    const translations = (await activeBundle.loaders[locale]?.()) ?? {};
    i18n.addResourceBundle(locale, "translation", translations, true, true);
  }
  await i18n.changeLanguage(locale);
}

export function updateLocalization(
  enabled: boolean,
  preferredLocale?: string | null,
): void {
  localizationConfig.setEnabled(enabled);
  if (!i18n.isInitialized) {
    return;
  }
  const locale = getPreferredLocale(SUPPORTED_LOCALES, preferredLocale);
  if (
    locale !== DEFAULT_LOCALE &&
    !i18n.hasResourceBundle(locale, "translation")
  ) {
    void setLocale(locale);
    return;
  }
  if (i18n.language !== locale) {
    void setLocale(locale);
  }
}
